import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Skeleton, Steps, Typography, message } from 'antd';
import documentService from '../../services/documentService';
import { isInspectionType } from './documentFindings';
import '../../assets/styles/documents.css';

const { Text } = Typography;

/** Below this many templates a filter box is more clutter than help. */
const SEARCH_THRESHOLD = 8;

/**
 * Two-step picker for raising a document: document type, then a template of
 * that type. Only what the user's entity can reach comes back -- the hierarchy
 * is resolved server-side.
 */
const CreateDocumentModal = ({
  open,
  onClose,
  onPick,
  companyId,
  typeName = null,
  title = 'Create Document',
  emptyText = 'You have no document templates available.',
}) => {
  const [step, setStep] = useState(0);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);

    documentService
      .getAvailableTemplates(companyId)
      .then((items) => {
        if (!cancelled) setTemplates(items);
      })
      .catch((error) => {
        console.error('Failed to load document templates', error);
        if (!cancelled) {
          setTemplates([]);
          message.error('Failed to load document templates');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  // Reset to step one whenever the picker is reopened.
  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedTypeId(null);
      setSearch('');
    }
  }, [open]);

  const pinnedToType = Boolean(typeName);

  const available = useMemo(() => {
    if (!pinnedToType) return templates;

    return templates.filter(
      (template) =>
        String(template.typeName ?? '').trim().toLowerCase() === typeName.trim().toLowerCase()
    );
  }, [templates, typeName, pinnedToType]);

  const types = useMemo(() => {
    const byId = new Map();

    available.forEach((template) => {
      
      if (isInspectionType(template.typeName)) return;

      const existing = byId.get(template.typeId);
      if (existing) {
        existing.count += 1;
        return;
      }
      byId.set(template.typeId, {
        id: template.typeId,
        name: template.typeName ?? '',
        count: 1,
      });
    });

    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [available]);

  const templatesForType = useMemo(
    () =>
      available
        .filter((template) => pinnedToType || template.typeId === selectedTypeId)
        .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''))),
    [available, selectedTypeId, pinnedToType]
  );

  const visibleTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templatesForType;

    return templatesForType.filter((template) =>
      [template.name, template.referencePrefix ?? ''].some((value) =>
        String(value).toLowerCase().includes(term)
      )
    );
  }, [templatesForType, search]);

  const chooseType = (typeId) => {
    setSelectedTypeId(typeId);
    setSearch('');
    setStep(1);
  };

  const renderTypes = () => {
    if (types.length === 0) {
      return <Empty description={emptyText} />;
    }

    return (
      <div className="create-document__list">
        {types.map((type) => (
          <button
            key={type.id}
            type="button"
            className="create-document__row"
            onClick={() => chooseType(type.id)}
          >
            <span className="create-document__row-name">{type.name || 'Unnamed type'}</span>
            <Text type="secondary" className="create-document__row-meta">
              {type.count} {type.count === 1 ? 'template' : 'templates'}
            </Text>
          </button>
        ))}
      </div>
    );
  };

  const renderTemplates = () => {
    if (templatesForType.length === 0) {
      return <Empty description={pinnedToType ? emptyText : 'No templates available for this type.'} />;
    }

    return (
      <>
        {templatesForType.length >= SEARCH_THRESHOLD && (
          <Input.Search
            className="create-document__search"
            placeholder="Search templates"
            allowClear
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}

        {visibleTemplates.length === 0 ? (
          <Empty description="No templates match that search." />
        ) : (
          <div className="create-document__list">
            {visibleTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                className="create-document__row"
                onClick={() => onPick?.({ id: template.id, name: template.name })}
              >
                <span className="create-document__row-name">
                  {template.name || 'Unnamed template'}
                </span>
                <span className="create-document__row-meta">
                  <Text type="secondary">
                    {template.fieldCount} {template.fieldCount === 1 ? 'field' : 'fields'}
                  </Text>
                </span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={window.innerWidth < 768 ? '95%' : 520}
      footer={!pinnedToType && step === 1 ? <Button onClick={() => setStep(0)}>Back</Button> : null}
    >
      {!pinnedToType && (
        <Steps
          size="small"
          responsive={false}
          current={step}
          className="create-document__steps"
          items={[{ title: 'Type' }, { title: 'Template' }]}
        />
      )}

      {loading ? (
        <Skeleton active />
      ) : !pinnedToType && step === 0 ? (
        renderTypes()
      ) : (
        renderTemplates()
      )}
    </Modal>
  );
};

export default CreateDocumentModal;
